import { useState } from 'react';
import { ChevronDown, Layers, Pencil } from 'lucide-react';
import { useTagLineFixer } from '@/lib/tagRules';

const ct = (n) => Number(n || 0).toFixed(2);

const Part = ({ p, i }) => (
  <li className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-slate-600" data-testid={`tag-merge-part-${i}`}>
    <span className="font-semibold text-slate-800">{p.name || '—'}</span>
    {p.note ? <span className="text-slate-500">· {p.note}</span> : null}
    <span className="tabular-nums">{ct(p.carats)} ct · {p.pieces} pcs</span>
    <span className="text-[10px] text-indigo-600">{p.rules.length ? `by rule: ${p.rules.join(' → ')}` : 'same display name'}</span>
  </li>
);

const Group = ({ m, gi }) => (
  <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5" data-testid={`tag-merge-group-${gi}`}>
    <div className="flex items-baseline justify-between gap-2 text-[12px]">
      <span className="font-bold text-slate-900">{m.classification}</span>
      <span className="tabular-nums text-slate-700">{ct(m.carats)} ct · {m.pieces} pcs · {m.partInfo.length} lines</span>
    </div>
    <ul className="mt-1 space-y-0.5 border-l-2 border-indigo-200 pl-2">{m.partInfo.map((p, i) => <Part key={i} p={p} i={`${gi}-${i}`} />)}</ul>
  </div>
);

// Super admin only: which tag lines were merged, with each portal line under it.
export const TagMergePreview = ({ materials, onFix, fixed }) => {
  const isSuper = useTagLineFixer();
  const [open, setOpen] = useState(true);
  const merged = (materials || []).filter((m) => (m.partInfo || []).length > 1);
  if (!isSuper || (!merged.length && !onFix)) return null;
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-2" data-testid="tag-merge-preview">
      <div className="flex items-center gap-2">
        {merged.length ? (
          <button type="button" onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-1.5 text-[12px] font-semibold text-indigo-900" data-testid="tag-merge-preview-toggle">
            <Layers size={14} /> Merged lines ({merged.length})
            <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        ) : <span className="flex-1 text-[12px] text-indigo-900">No merged lines on this tag</span>}
        {fixed ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid="tag-fix-badge">Corrected by super admin</span> : null}
        {onFix ? <button type="button" onClick={onFix} className="flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2 py-1 text-[11px] font-semibold text-indigo-800 hover:bg-indigo-100" data-testid="tag-fix-open"><Pencil size={12} /> Correct tag lines</button> : null}
      </div>
      {open && merged.length ? <div className="mt-2 grid gap-1.5 sm:grid-cols-2">{merged.map((m, gi) => <Group key={m.key} m={m} gi={gi} />)}</div> : null}
    </div>
  );
};
